// conflict-detect.js — the shared-workspace note-merge SAFE FLOOR (Phase 1).
//
// A PURE classifier for an inbound remote note update. It NEVER touches the
// DOM, marked, CodeMirror, or storage — it operates only on the plaintext
// strings handed to it and returns a decision the (impure) sync hooks act on.
// Both the PowerSync path (core/powersync-bootstrap.js) and the IndexedDB /
// cross-window path (core/app-init.js) route their "remote update arrived for
// the open note" branch through resolveInboundConflict so the two stay in
// lock-step — this is what resolves the historic trim() drift between them.
//
// The replaced behaviour was the two no-op "keeping your unsaved edits" toasts:
// an inbound remote update either silently overwrote concurrent local edits or
// was silently dropped. The floor below is a never-lose, never-corrupt
// classifier + conflicted-copy fork.
//
// 3-way base = the passed `base` (app-state.getLastRemoteContent) — the last
// content this device confirmed as received from remote. Classification:
//
//   remote === base                  -> 'ignore'      (echo of what we saw)
//   !hasUnsavedEdits                 -> 'fastForward' (safe replace; no local divergence)
//   remote === local                 -> 'rebase'      (both sides converged; advance baselines)
//   true divergence (unsaved local && remote !== local && remote !== base):
//     -> 'fork'  (never risk a silent overwrite; we keep BOTH sides)
//
// FORK is the conservative floor for every real conflict. The auto-merge engine
// (notes/merge3.js) now slots in AHEAD of that fork — but the attempt is async
// and heavy (durable-base read + dynamic merge3 import), so it lives in the
// IMPURE executor (conflict-apply.js), NOT here. This classifier stays PURE and
// sync: for a true divergence it still returns `action: 'fork'`, but it also
// tags the decision with `mergeable` — a cheap, pure prerequisite check the
// executor consults before bothering to try a merge. `mergeable` is true only
// when a 3-way base exists AND the note name is not deny-listed. When the
// executor's merge attempt declines or throws, the fork below is the unchanged
// fallback, so this floor's contract is preserved exactly.
//
// `surviving: 'local'` documents which side stays live in the editor on a fork:
// local. The remote lands as a deterministic sidecar copy; local keeps
// uploading via the normal autosave / LWW path so neither side is lost.

import { hashSource } from '../editor/render-core/hash.js';
// Daily-note name + metadata-note constants come from the pure utils/ leaf so
// this guard and calendar-sync (the writer) share ONE source of truth — see
// utils/calendar-format.js. The leaf imports nothing, so this module stays a
// pure leaf (no schedule/Settings pull). DAILY_NOTE_RE here is the capturing
// form; isDenyListed only `.test()`s it, for which the capture is harmless.
import { CALENDAR_METADATA_NOTE, DAILY_NOTE_RE } from '../utils/calendar-format.js';

/** Inputs to the inbound-conflict classifier (all content is plaintext). */
export interface ResolveInboundArgs {
  /** Display name of the open note. */
  name: string;
  /** 3-way base = last content confirmed common to both sides (null = cold start). */
  base: string | null;
  /** Live editor content. */
  local: string;
  /** Inbound remote content (storage read). */
  remote: string;
  /** Last content this device wrote (reserved for the auto-merge follow-up). */
  saved: string | null;
  /** Whether `local` diverges from `saved`. */
  hasUnsavedEdits: boolean;
}

/**
 * Inputs to the impure conflict EXECUTORS (applyInboundConflict shell +
 * applyInboundConflictCore). Superset of the pure classifier's args: the same
 * content fields plus the active NoteStorage handle and the generation-recheck
 * predicate the storage/editor writes are gated on. Typed (not `any`) so a
 * mis-named field at a call site — e.g. `hasUnsavedEdit` or `removed` — is a
 * compile error instead of a silent `undefined` that would mis-classify a
 * conflict and risk overwriting concurrent local edits.
 */
export interface ApplyInboundConflictArgs extends ResolveInboundArgs {
  /** Active NoteStorage (window.NoteStorage / IDB); dynamic surface. */
  NS: any;
  /** Returns true if a newer loadNote superseded us; writes bail when it does. */
  recheckGen?: () => boolean;
}

/** The classifier's decision. `action` is the data-loss floor's contract;
 *  `mergeable` advises the impure executor whether to TRY an auto-merge before
 *  it falls through to the fork. The classifier never merges — it only signals. */
export interface InboundConflictDecision {
  action: 'ignore' | 'fastForward' | 'rebase' | 'fork';
  forkName?: string;
  surviving?: 'local';
  mergeable?: boolean;
}

// Notes that must NEVER auto-merge by NAME (the executor adds the content-level
// '## Events'-body guard where it actually has the bytes):
//   • '.calendar_metadata' — the hidden JSON sync-state note. A byte-imperfect
//     merge here desyncs every calendar entry; always fork instead.
//   • daily-note bodies (`NNNNNN Daily Note`) — they carry calendar-managed
//     event lines under '## Events'; the executor's structural soundness check
//     guards the event lines, but we deny-list the whole daily note by name as
//     the cheap first gate so a base-less or non-event divergence still forks.
//
// Exported so the impure executor (conflict-apply.js) shares this single source
// of truth rather than re-deriving the rule.
/**
 * @param {any} name - a note name.
 * @returns {boolean} true if the note must never auto-merge.
 */
export function isDenyListed(name: string): boolean {
  if (name === CALENDAR_METADATA_NOTE) return true;
  if (typeof name === 'string' && DAILY_NOTE_RE.test(name)) return true;
  return false;
}

// Deterministic, idempotent conflicted-copy name. The suffix is derived from
// the FNV-1a hash of the remote content (NOT the wall clock) so the SAME
// (name, remote) pair yields the SAME copy name on every device — two devices
// that fork the same inbound update converge on one sidecar instead of
// spawning N copies. base36 FNV output trimmed to a short stable token.
/**
 * @param {string} name
 * @param {any} remote - the inbound remote content.
 * @returns {string} a deterministic conflicted-copy name.
 */
export function conflictedCopyName(name: string, remote: string): string {
  const shortHash = hashSource(remote == null ? '' : String(remote)).slice(0, 6);
  return `${name} (conflicted copy ${shortHash})`;
}

// Inverse of conflictedCopyName: recover the ORIGINAL base name from a conflicted-copy name, or
// null when `name` isn't one. The token is base36 FNV output ([0-9a-z], up to 7 chars); the greedy
// `(.*)` lets a base name that itself contains parentheses round-trip. The Labyrinth Thread uses
// this to pair a sidecar copy back with its surviving original so it can show a diff + merge.
const _CONFLICTED_COPY_RE = / \(conflicted copy [0-9a-z]{1,7}\)$/;
export function parseConflictedCopyName(name: string): string | null {
  if (typeof name !== 'string') return null;
  const m = _CONFLICTED_COPY_RE.exec(name);
  return m ? name.slice(0, m.index) : null;
}

/** Pair every conflicted-copy note with its surviving original. A copy whose base note no longer
 *  exists (the original was trashed) is skipped — there's nothing to diff/merge against. Stable
 *  order (by base, then copy) so the Thread walk is deterministic. */
export function findConflictedCopies(
  notes: ReadonlyArray<{ name: string }>
): Array<{ base: string; copy: string }> {
  const names = new Set(notes.map((n) => n.name));
  const pairs: Array<{ base: string; copy: string }> = [];
  for (const n of notes) {
    const base = parseConflictedCopyName(n.name);
    if (base != null && names.has(base)) pairs.push({ base, copy: n.name });
  }
  pairs.sort((a, b) => a.base.localeCompare(b.base) || a.copy.localeCompare(b.copy));
  return pairs;
}

/**
 * Classify an inbound remote note update against the open note's state.
 *
 * @param {object} args
 * @param {string}  args.name             Display name of the open note.
 * @param {?string} args.base             3-way base = last content confirmed
 *                                        received from remote (getLastRemoteContent).
 *                                        null on a cold start (no base hydrated yet).
 * @param {string}  args.local            Live editor content (NoteEditor.getContent()).
 * @param {string}  args.remote           Inbound remote content (storage read).
 * @param {?string} args.saved            Last content this device wrote
 *                                        (getLastSavedContent); reserved for the
 *                                        future auto-merge, not used by the floor.
 * @param {boolean} args.hasUnsavedEdits  Whether `local` diverges from `saved`.
 * @returns {{ action: 'ignore'|'fastForward'|'rebase'|'fork',
 *             forkName?: string, surviving?: 'local', mergeable?: boolean }}
 *          On the true-divergence 'fork' decision, `mergeable` tells the impure
 *          executor whether an auto-merge is even worth attempting BEFORE it
 *          forks: true iff a 3-way base exists (base != null) AND the note is
 *          not deny-listed by name. The classifier never merges itself — it
 *          only signals; the executor reads the durable base, runs merge3, and
 *          falls through to this fork on any decline/throw.
 */
export function resolveInboundConflict({
  name,
  base,
  local,
  remote,
  saved,
  hasUnsavedEdits,
}: ResolveInboundArgs): InboundConflictDecision {
  // void the reserved param so the signature is stable for the auto-merge
  // follow-up without tripping no-unused-vars on the floor.
  void saved;

  // Echo of content we've already absorbed — nothing to do.
  if (remote === base) {
    return { action: 'ignore' };
  }

  // No unsaved local divergence: the existing safe replace is correct. This is
  // the common "another device edited a note I'm only viewing" case.
  if (!hasUnsavedEdits) {
    return { action: 'fastForward' };
  }

  // Both sides independently converged on the same text — just advance the
  // baselines, no banner, keep the editor as-is.
  if (remote === local) {
    return { action: 'rebase' };
  }

  // True divergence: unsaved local edits AND remote !== local AND remote !== base.
  // The classifier's action stays 'fork' — that is the floor's unchanged
  // contract and the executor's fallback on any merge decline/throw. We tag the
  // decision with `mergeable` so the (impure) executor knows whether to TRY an
  // auto-merge first:
  //   • base != null && !isDenyListed(name) — auto-merge is worth attempting;
  //     the executor reads the durable base, runs merge3, and only on a clean +
  //     structurally-sound result upgrades the action to 'merge'. Otherwise it
  //     falls through to this fork.
  //   • base == null — cold start with no base; we can't reason about a 3-way
  //     merge without one, so fork is the only safe choice (mergeable: false).
  //   • deny-listed name — never auto-merge (mergeable: false); always fork.
  // Either way the floor never silently overwrites local.
  return {
    action: 'fork',
    forkName: conflictedCopyName(name, remote),
    surviving: 'local',
    mergeable: base != null && !isDenyListed(name),
  };
}
